/**
 * Privacy & Compliance Acknowledgement Modal
 *
 * Displayed during signup flow before email verification.
 * User must agree to privacy policy and terms before account creation.
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Shield, Lock, Eye, Database, UserCheck } from "lucide-react";

interface PrivacyAcknowledgementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAgree: () => void;
  isLoading?: boolean;
}

export default function PrivacyAcknowledgementModal({
  isOpen,
  onClose,
  onAgree,
  isLoading = false,
}: PrivacyAcknowledgementModalProps) {
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const canProceed = agreeToTerms;

  const handleAgree = () => {
    if (canProceed) {
      onAgree();
    }
  };

  // Reset state when modal closes
  const handleClose = () => {
    setAgreeToTerms(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl">Privacy & Compliance Acknowledgement</DialogTitle>
              <DialogDescription>
                Please review and agree to our terms before creating your account
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 pr-4 overflow-y-auto">
          <div className="space-y-6 text-sm text-gray-700">
            {/* Introduction */}
            <section>
              <p className="leading-relaxed">
                Welcome to Trade Loop! Before you get started, here's a quick overview of
                how we handle your information. We believe in being upfront and keeping things simple.
              </p>
            </section>

            {/* What We Need From You */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-gray-900">What We Need From You</h3>
              </div>
              <p className="mb-2 text-gray-600">To create your account and connect you with partners, we'll need:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Your contact details (name, email, phone number)</li>
                <li>Your business info (company name, trade, service areas)</li>
                <li>Your profile details (bio and photos to help partners find you)</li>
              </ul>
            </section>

            {/* How We Use Your Info */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-gray-900">How We Use Your Info</h3>
              </div>
              <p className="mb-2 text-gray-600">We use your information to:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Help you connect with business partners in your trade</li>
                <li>Send you important updates about your account</li>
                <li>Keep the platform safe and secure</li>
                <li>Improve Trade Loop based on how people use it</li>
              </ul>
            </section>

            {/* Who Can See Your Info */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <UserCheck className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Who Can See Your Info</h3>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Other members</strong> can see your public business profile</li>
                <li><strong>Our team</strong> may access your info to help with support</li>
                <li><strong>Legal authorities</strong> only if required by law</li>
              </ul>
              <p className="mt-2 text-green-700 font-medium">
                We never sell your personal information to anyone.
              </p>
            </section>

            {/* Keeping Your Data Safe */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Keeping Your Data Safe</h3>
              </div>
              <p>
                We use encryption and secure servers to protect your information.
                Your password is never stored in plain text, and we regularly review
                our security practices.
              </p>
            </section>

            {/* You're In Control */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-gray-900">You're In Control</h3>
              </div>
              <p className="mb-2">You can always:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Update or correct your information anytime</li>
                <li>Download a copy of your data</li>
                <li>Delete your account and all associated data</li>
                <li>Opt-out of marketing emails</li>
              </ul>
            </section>

            {/* Quick Summary */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">Quick Summary</h3>
                  <p className="text-blue-800">
                    We only ask for information needed to run Trade Loop. Your business profile
                    will be visible to other members so they can connect with you. You can
                    delete your account anytime.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="text-gray-600">
              <p>
                For questions about these terms or your personal data, please contact us at{" "}
                <a href="mailto:privacy@tradeloop.com" className="text-blue-600 hover:underline">
                  privacy@tradeloop.com
                </a>
              </p>
              <p className="mt-2 text-xs">
                Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            </section>
          </div>
        </ScrollArea>

        {/* Agreement Checkbox */}
        <div className="flex-shrink-0 border-t pt-4 mt-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="agree-terms"
              checked={agreeToTerms}
              onCheckedChange={(checked) => setAgreeToTerms(checked === true)}
              disabled={isLoading}
            />
            <label htmlFor="agree-terms" className="text-sm text-gray-700 cursor-pointer leading-tight">
              I agree to the <span className="text-blue-600 font-medium">Terms of Service</span> and{" "}
              <span className="text-blue-600 font-medium">Privacy Policy</span>
            </label>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 mt-4 gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAgree}
            disabled={!canProceed || isLoading}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl"
          >
            {isLoading ? "Creating Account..." : "I Agree & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
